/**
 * #3132 — in-app confirmation modal for the Deploy-to-TV action.
 *
 * Replaces the native `window.confirm(...)` that used to gate
 * `handleDeploy` in `Workspace.tsx`. The native dialog was the root
 * cause of the issue's "silent no-op": it is not a DOM node (so
 * DOM-driven probes cannot see it) and CDP/Playwright auto-dismisses
 * native dialogs, silently DECLINING the deploy with zero feedback.
 * A DOM-rendered modal is visible, stylable, consistent across the
 * desktop shell and the cloud SPA, and automatable.
 *
 * The visible Cancel button + Escape key + backdrop click all close
 * the modal without firing the confirm callback. Only the explicit
 * "Deploy" CTA fires `onConfirm`. Mirrors `BackToPlanConfirmModal`
 * (focus management, focus trap, drag-region carve-out).
 */

import { useEffect, useRef, type ReactElement } from 'react';

export const DEPLOY_CONFIRM_MODAL_TESTID = 'deploy-confirm-modal';
export const DEPLOY_CONFIRM_CONFIRM_TESTID = 'deploy-confirm-modal-confirm';
export const DEPLOY_CONFIRM_CANCEL_TESTID = 'deploy-confirm-modal-cancel';

export interface DeployConfirmModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function DeployConfirmModal({
  open,
  onCancel,
  onConfirm,
}: DeployConfirmModalProps): ReactElement | null {
  // Cancel gets initial focus on open — moving focus to the SAFE
  // action reduces the chance a stray Enter immediately commits +
  // pushes. The trigger element's previous focus is restored on close.
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Bugbot (PR #3237): the old blocking window.confirm was itself a
  // re-entrancy barrier — nothing could interleave while it was up.
  // This DOM modal is not: a double-click (or Enter-key repeat) on the
  // Deploy CTA fires onConfirm twice in the same tick, BEFORE the
  // parent's async state updates (modal close, deployState) commit —
  // which double-fired publish:run. React state is async, so the latch
  // must be SYNCHRONOUS: a ref that admits exactly one confirm per
  // open. Reset when the modal (re)opens so a later legitimate deploy
  // can confirm again.
  const confirmFiredRef = useRef(false);
  useEffect(() => {
    if (open) confirmFiredRef.current = false;
  }, [open]);
  const handleConfirmClick = (): void => {
    if (confirmFiredRef.current) return;
    confirmFiredRef.current = true;
    onConfirm();
  };

  // Escape closes the modal — counts as cancel, NOT confirm.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onCancel]);

  // Focus management for screen readers / keyboard users. On open,
  // capture the previously focused element, move focus into the dialog
  // (cancel button), and restore the original focus on close.
  useEffect(() => {
    if (!open) return;
    if (typeof document === 'undefined') return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // queueMicrotask so the ref is attached before we read it.
    queueMicrotask(() => {
      cancelButtonRef.current?.focus();
    });
    return () => {
      previouslyFocused?.focus?.();
    };
  }, [open]);

  // Simple Tab/Shift+Tab focus trap — sufficient for a two-button
  // modal (same lightweight trap as BackToPlanConfirmModal).
  useEffect(() => {
    if (!open) return;
    if (typeof document === 'undefined') return;
    const trap = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusables = dialog.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !dialog.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !dialog.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', trap);
    return () => window.removeEventListener('keydown', trap);
  }, [open]);

  if (!open) return null;

  return (
    <div
      // z-[60] + app-region-no-drag: same stacking + drag-region
      // carve-out rationale as BackToPlanConfirmModal (issue #965 —
      // the header's -webkit-app-region: drag would otherwise bleed
      // through the backdrop and eat the click-to-cancel gesture).
      className="app-region-no-drag fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onCancel}
      data-testid={DEPLOY_CONFIRM_MODAL_TESTID}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="deploy-confirm-modal-title"
        aria-describedby="deploy-confirm-modal-body"
        className="w-full max-w-md rounded-xl border border-white/10 bg-crucible-surface p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="deploy-confirm-modal-title" className="mb-2 text-lg font-semibold text-white">
          Deploy to TV?
        </h2>
        <p id="deploy-confirm-modal-body" className="mb-5 text-sm text-white/60">
          Deploying commits any uncommitted local changes, pushes them to GitHub, and triggers the
          CI deploy to the TV cluster.
        </p>
        <div className="flex justify-end gap-3">
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onCancel}
            data-testid={DEPLOY_CONFIRM_CANCEL_TESTID}
            className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/60 transition-colors hover:border-white/20 hover:text-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirmClick}
            data-testid={DEPLOY_CONFIRM_CONFIRM_TESTID}
            className="rounded-lg bg-emerald-500/90 px-4 py-2 text-sm font-medium text-white transition-opacity hover:bg-emerald-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
          >
            Deploy
          </button>
        </div>
      </div>
    </div>
  );
}
